import { create } from 'zustand';
import { customersAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';
import { Customer, CustomerStats, CreateCustomerRequest, UpdateCustomerRequest, ConvertOrderToCustomerRequest } from '../types';

interface CustomerState {
  customers: Customer[];
  stats: CustomerStats;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCustomers: (status?: string, projectId?: number) => Promise<void>;
  fetchStats: (projectId?: number) => Promise<void>;
  createCustomer: (data: CreateCustomerRequest) => Promise<Customer>;
  updateCustomer: (customerId: number, data: UpdateCustomerRequest) => Promise<Customer>;
  deleteCustomer: (customerId: number) => Promise<void>;
  activateCustomer: (customerId: number) => Promise<Customer>;
  suspendCustomer: (customerId: number) => Promise<Customer>;
  terminateCustomer: (customerId: number) => Promise<Customer>;
  convertFromOrder: (orderId: number, data: ConvertOrderToCustomerRequest) => Promise<Customer>;
}

export const useCustomerStore = create<CustomerState>((set, get) => ({
  customers: [],
  stats: {
    total: 0,
    active: 0,
    suspended: 0,
    terminated: 0,
    pending_installation: 0,
  },
  isLoading: false,
  error: null,

  fetchCustomers: async (status?: string, projectId?: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.getAll(status, projectId);
      set({ customers: response.data, isLoading: false });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Nepodarilo sa načítať zákazníkov.'),
        isLoading: false
      });
    }
  },

  fetchStats: async (projectId?: number) => {
    try {
      const response = await customersAPI.getStats(projectId);
      set({ stats: response.data });
    } catch (error: any) {
      console.error('Failed to fetch customer stats:', error);
    }
  },

  createCustomer: async (data: CreateCustomerRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.create(data);
      const newCustomer = response.data;
      
      set(state => ({
        customers: [newCustomer, ...state.customers],
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
      
      return newCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa vytvoriť zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  updateCustomer: async (customerId: number, data: UpdateCustomerRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.update(customerId, data);
      const updatedCustomer = response.data;
      
      set(state => ({
        customers: state.customers.map(c => 
          c.id === customerId ? updatedCustomer : c
        ),
        isLoading: false
      }));
      
      // Refresh stats if status changed
      if (data.status) {
        get().fetchStats();
      }
      
      return updatedCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  deleteCustomer: async (customerId: number) => {
    set({ isLoading: true, error: null });
    try {
      await customersAPI.delete(customerId);
      
      set(state => ({
        customers: state.customers.filter(c => c.id !== customerId),
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa zmazať zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  activateCustomer: async (customerId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.activate(customerId);
      const updatedCustomer = response.data;
      
      set(state => ({
        customers: state.customers.map(c => 
          c.id === customerId ? updatedCustomer : c
        ),
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
      
      return updatedCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa aktivovať zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  suspendCustomer: async (customerId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.suspend(customerId);
      const updatedCustomer = response.data;
      
      set(state => ({
        customers: state.customers.map(c => 
          c.id === customerId ? updatedCustomer : c
        ),
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
      
      return updatedCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa pozastaviť zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  terminateCustomer: async (customerId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.terminate(customerId);
      const updatedCustomer = response.data;
      
      set(state => ({
        customers: state.customers.map(c => 
          c.id === customerId ? updatedCustomer : c
        ),
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
      
      return updatedCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa ukončiť zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },

  convertFromOrder: async (orderId: number, data: ConvertOrderToCustomerRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersAPI.convertFromOrder(orderId, data);
      const newCustomer = response.data;
      
      set(state => ({
        customers: [newCustomer, ...state.customers],
        isLoading: false
      }));
      
      // Refresh stats
      get().fetchStats();
      
      return newCustomer;
    } catch (error: any) {
      set({ 
        error: getErrorMessage(error, 'Nepodarilo sa previesť objednávku na zákazníka.'),
        isLoading: false 
      });
      throw error;
    }
  },
}));

